import { useTeamContext } from "@/components/context-providers/TeamProvider";
import { useRouteInfo } from "@/utils/hooks/url/use-route-info";
import { useMemo } from "react";

/**
 * Resolution of the url's environment id against the GetTeam response.
 *
 * `"loading"` means we cannot answer yet (no GetTeam response in hand); `"ok"` means either
 * the environment resolved or the question is not ours to answer.
 */
export type EnvironmentLookupResult =
  | { status: "loading" }
  | { status: "ok" }
  | { status: "environment-not-found" }
  /** The environment exists, but under a different project in the same team. */
  | { status: "project-mismatch"; correctProjectId: string };

/**
 * Structural shape of the parts of the GetTeam response we read, so callers and
 * tests can pass a plain object instead of constructing a `Team` proto.
 */
export interface EnvironmentLookupTeam {
  projects: readonly {
    id: string;
    environments: readonly { id: string }[];
  }[];
}

export interface EnvironmentLookupArgs {
  team: EnvironmentLookupTeam | undefined;
  projectId: string | undefined;
  environmentId: string | undefined;
}

const LOADING: EnvironmentLookupResult = { status: "loading" };
const OK: EnvironmentLookupResult = { status: "ok" };
const ENVIRONMENT_NOT_FOUND: EnvironmentLookupResult = { status: "environment-not-found" };

/**
 * Resolves the url's environment id against a loaded GetTeam response.
 */
export function getEnvironmentLookupResult({
  team,
  projectId,
  environmentId,
}: EnvironmentLookupArgs): EnvironmentLookupResult {
  if (projectId == null || environmentId == null) {
    return OK;
  }

  if (team == null) {
    return LOADING;
  }

  const project = team.projects.find((p) => p.id === projectId);
  if (project == null) {
    // "url project missing from the team" is precisely the signal
    // `getAutoImpersonationDecision` treats as "the impersonation cookie points at the
    // wrong team", so the environment is unknowable here rather than missing — we are
    // holding some other team's projects. Reporting "ok" keeps the banner from flashing
    // while auto-impersonation re-points the cookie and refetches. The other
    // auto-impersonation trigger (a PermissionDenied GetTeam) leaves `team` undefined
    // and is already covered by the "loading" branch above.
    return OK;
  }

  if (project.environments.some((environment) => environment.id === environmentId)) {
    return OK;
  }

  // The environment turning up under a sibling project means the url pairs a real
  // project with a real environment that do not belong together, which is a different
  // failure (and a recoverable one) from an environment that is nowhere in the team.
  const owningProject = team.projects.find((p) =>
    p.environments.some((environment) => environment.id === environmentId),
  );
  return owningProject != null
    ? { status: "project-mismatch", correctProjectId: owningProject.id }
    : ENVIRONMENT_NOT_FOUND;
}

/**
 * Reports whether the environment in the url exists in the current team's copy of the
 * url's project. Deliberately reads GetTeam directly rather than through
 * `useRouteAndTeamInfo`, which additionally waits on GetTeamPermissions.
 */
export function useEnvironmentLookupResult(): EnvironmentLookupResult {
  const { projectId, environmentId } = useRouteInfo();
  const { team } = useTeamContext();

  return useMemo(
    () => getEnvironmentLookupResult({ team, projectId, environmentId }),
    [team, projectId, environmentId],
  );
}
